var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo } from 'react';
import { FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { CreateDareFormSchema } from 'root/src/client/web/record/CreatorData/CreatorDare/CreateCreatorDare';
import { CreatorEventFulfilledDareStatus } from 'root/src/shared/dares/creatorEventFulfilledDare.types';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Textarea } from 'root/src/client/v2/common/components/ui/textarea';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
const TitleField = () => {
    var _a;
    const form = useFormContext();
    const { field, fieldState } = useController({ name: 'title', control: form.control });
    return (<Field label="Title" htmlFor="ed-title" error={(_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message}>
			<Input id="ed-title" maxLength={50} placeholder="Short and catchy" error={Boolean(fieldState.error)} {...field}/>
		</Field>);
};
const DescriptionField = () => {
    var _a;
    const form = useFormContext();
    const { field, fieldState } = useController({ name: 'description', control: form.control });
    return (<Field label="Description" htmlFor="ed-desc" error={(_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message}>
			<Textarea id="ed-desc" rows={3} maxLength={200} placeholder="What should the creator do?" error={Boolean(fieldState.error)} {...field}/>
		</Field>);
};
export const RedesignEditDareModal = memo(({ dare, onEdit }) => {
    const modal = useRedesignModal();
    const save = useAsyncAction();
    const disable = useAsyncAction();
    const form = useForm({
        resolver: zodResolver(CreateDareFormSchema),
        defaultValues: { title: dare.title, description: dare.description },
    });
    const editMutation = trpc.dares.updateEventFulfilledDare.useMutation();
    const onSubmit = form.handleSubmit((data) => {
        void save.run(() => __awaiter(void 0, void 0, void 0, function* () {
            yield editMutation.mutateAsync({ id: dare.id, title: data.title, description: data.description });
            yield onEdit();
        }), {
            onSuccess: modal.hide,
            toast: {
                loading: 'Saving changes...',
                success: 'Dare updated.',
                error: caught => (caught instanceof Error ? caught.message : 'Could not update dare'),
            },
        });
    });
    const onDisable = () => {
        void disable.run(() => __awaiter(void 0, void 0, void 0, function* () {
            yield editMutation.mutateAsync({ id: dare.id, status: CreatorEventFulfilledDareStatus.Disabled });
            yield onEdit();
        }), {
            onSuccess: modal.hide,
            toast: {
                loading: 'Disabling dare...',
                success: 'Dare disabled.',
                error: caught => (caught instanceof Error ? caught.message : 'Could not disable dare'),
            },
        });
    };
    return (<RedesignModalShell className="sm:max-w-md">
			<div className="flex w-full flex-col items-center gap-1 text-center">
				<h2 className="font-display text-xl font-bold text-foreground">Edit your dare</h2>
				<p className="text-sm text-muted-foreground">Update the details below or disable this dare so it no longer appears.</p>
			</div>

			<FormProvider {...form}>
				<form onSubmit={onSubmit} className="flex w-full flex-col gap-5">
					<TitleField />
					<DescriptionField />
					<Button type="submit" variant="brand" loading={save.loading} success={save.success} error={save.error} disabled={disable.loading} className="w-full rounded-lg text-sm font-semibold">
						Save changes
					</Button>
					<Button type="button" variant="outline" onClick={onDisable} loading={disable.loading} success={disable.success} error={disable.error} disabled={save.loading} className="w-full rounded-lg text-sm font-semibold text-destructive hover:bg-destructive/10">
						Disable dare
					</Button>
				</form>
			</FormProvider>
		</RedesignModalShell>);
});
